import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { logo } from "../../assets";
import { useScrollOptimized } from "../../hooks/useScrollOptimized";
import { GAMES, DEFAULT_GAME, HOT_GAME, parseGameFromUrl } from "../../utils/gameParam";
import { SITE } from "../../config/sites";
import "./TopBar.css";

export default function TopBar() {
  const isScrolled = useScrollOptimized(100, 16);
  const { pathname, search, hash } = useLocation();

  /* Dải chọn game chỉ có nghĩa ở trang chủ — nơi có danh sách acc để lọc */
  const isHome = pathname === "/";
  const activeGame = parseGameFromUrl(search, hash) || DEFAULT_GAME;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDownloadOpen, setIsDownloadOpen] = useState(false);
  const [isMobileDownloadOpen, setIsMobileDownloadOpen] = useState(false);
  const downloadRef = useRef(null);
  const menuToggleRef = useRef(null);
  const menuCloseRef = useRef(null);
  const navRef = useRef(null);
  const [navHeight, setNavHeight] = useState(0);

  // Đo chiều cao navbar lúc còn nằm trong luồng. Khi cuộn quá 100px navbar
  // chuyển sang position:fixed — không có khối giữ chỗ thì toàn bộ nội dung
  // tụt lên đúng bằng chiều cao đó, gây giật và làm mọi phép cuộn tới mục bị lố.
  useEffect(() => {
    const el = navRef.current;
    if (!el || isScrolled) return;
    /* KHÔNG gọi el.offsetHeight ở đây. Đọc hình học ngay trong effect chạy sau
       mount là buộc trình duyệt layout lại giữa lúc nó chưa layout xong lần đầu
       — Lighthouse đo 66ms forced reflow ở đúng dòng cũ trên production.
       Không cần đọc: ResizeObserver bắn callback ngay khi observe() với kích
       thước ban đầu, tức số đo đầu tiên tới miễn phí qua đường bên dưới. */
    if (typeof ResizeObserver === "undefined") {
      /* Trình duyệt không có ResizeObserver: đo một lần ở khung hình sau, hoãn
         được vì con số này chỉ dùng cho khối giữ chỗ lúc ĐÃ cuộn (xem
         `isScrolled && navHeight` phía dưới). */
      const raf = window.requestAnimationFrame(() => setNavHeight(el.offsetHeight));
      return () => window.cancelAnimationFrame(raf);
    }
    /* Đọc số đo TỪ entry của observer, không gọi lại offsetHeight.
       `offsetHeight` bên trong callback của ResizeObserver là một forced reflow
       (đọc hình học ngay sau khi style vừa bị đánh dấu bẩn) — Lighthouse chỉ
       đúng chỗ này trong audit "Forced reflow". borderBoxSize là con số trình
       duyệt VỪA tính xong nên lấy nó không tốn lượt tính lại nào. */
    const observer = new ResizeObserver((entries) => {
      const box = entries[0] && entries[0].borderBoxSize && entries[0].borderBoxSize[0];
      if (box) setNavHeight(box.blockSize);
      else setNavHeight(el.offsetHeight);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [isScrolled]);

  // Đóng dropdown desktop khi click bên ngoài
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (downloadRef.current && !downloadRef.current.contains(e.target)) {
        setIsDownloadOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Escape đóng drawer/dropdown (WCAG 2.1.2 — không bẫy bàn phím)
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key !== "Escape") return;
      if (isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
        if (menuToggleRef.current) menuToggleRef.current.focus();
      }
      setIsDownloadOpen(false);
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isMobileMenuOpen]);

  // Mở drawer → focus nút đóng để bàn phím thao tác được ngay
  useEffect(() => {
    if (isMobileMenuOpen && menuCloseRef.current) {
      menuCloseRef.current.focus();
    }
  }, [isMobileMenuOpen]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Tự đóng sidebar khi resize lên desktop.
  // Mốc 1024 khớp với CSS (desktop nav ẩn ở max-width: 1023px) —
  // trước đây CSS dùng 992px còn JS dùng >= 992 nên tại đúng 992px không có nav nào.
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <>
      <section ref={navRef} className={`navbar ${isScrolled ? "navbar--scrolled" : ""}`}>
        <div className="navbar__section navbar__icon">
          <Link to="/" aria-label="HOANGLE CF — Trang chủ" onClick={closeMobileMenu}>
            <img className="navbar__logo" src={logo} alt="HOANGLE CF - Shop Acc Game Uy Tín" width="260" height="154" />
          </Link>
        </div>

        <nav className="navbar__section navbar_group" aria-label="Điều hướng chính">
          <Link to="/" className={pathname === "/" ? "active" : ""} aria-current={pathname === "/" ? "page" : undefined}>
            <span>Trang Chủ</span>
          </Link>
          <Link to="/cong-dong" className={pathname === "/cong-dong" ? "active" : ""} aria-current={pathname === "/cong-dong" ? "page" : undefined}>
            <span>Cộng Đồng</span>
          </Link>
          <Link to="/hong-bien" className={pathname === "/hong-bien" ? "active" : ""} aria-current={pathname === "/hong-bien" ? "page" : undefined}>
            <span>Hóng Biến</span>
          </Link>
          <Link to="/huong-dan" className={pathname === "/huong-dan" ? "active" : ""} aria-current={pathname === "/huong-dan" ? "page" : undefined}>
            <span>Hướng Dẫn</span>
          </Link>
          <div className="nav-dropdown" ref={downloadRef}>
            <button
              className="nav-dropdown__toggle"
              onClick={() => setIsDownloadOpen(!isDownloadOpen)}
              aria-expanded={isDownloadOpen}
              aria-controls="download-menu"
              aria-haspopup="true"
            >
              <span>Tải Phần Mềm</span>
              <i className={`nav-dropdown__arrow ${isDownloadOpen ? "is-open" : ""}`} aria-hidden="true" />
            </button>
            <div id="download-menu" className={`nav-dropdown__menu ${isDownloadOpen ? "is-open" : ""}`}>
              <a href="/downloads/AnyDesk.exe" download rel="nofollow" className="nav-dropdown__item">
                <span className="nav-dropdown__item-sub">Remote Desktop</span>
                <span className="nav-dropdown__item-title">Tải AnyDesk</span>
              </a>
              <a href="/downloads/Aweray_Remote.exe" download rel="nofollow" className="nav-dropdown__item">
                <span className="nav-dropdown__item-sub">Remote Desktop</span>
                <span className="nav-dropdown__item-title">Tải AweSun</span>
              </a>
              <a href="/downloads/zApp01092026.rar" download rel="nofollow" className="nav-dropdown__item">
                <span className="nav-dropdown__item-sub">Thuê Acc CF</span>
                <span className="nav-dropdown__item-title">App Thuê Acc</span>
              </a>
            </div>
          </div>
        </nav>

        <div className="navbar__section navbar__title">
          <a
            className="cus_link hl-btn hl-btn--primary hl-btn--sm"
            href="https://www.facebook.com/ThueAcCF"
            target="_blank"
            rel="noreferrer noopener"
            onClick={() => {
              window.gtag_report_conversion_contact?.();
              if (SITE.id === "com") window.gtag_report_conversion_contact2?.();
            }}
          >
            Liên Hệ Admin
          </a>
        </div>

        {/* Mobile Menu Toggle Button */}
        <button
          ref={menuToggleRef}
          className={`mobile-menu-toggle ${isMobileMenuOpen ? "is-open" : ""}`}
          onClick={toggleMobileMenu}
          aria-label={isMobileMenuOpen ? "Đóng menu" : "Mở menu"}
          aria-expanded={isMobileMenuOpen}
          aria-controls="mobile-menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </section>

      {/* Khối giữ chỗ đúng bằng navbar khi navbar rời khỏi luồng (fixed) */}
      {isScrolled && navHeight > 0 && (
        <div className="navbar-spacer" style={{ height: navHeight }} aria-hidden="true" />
      )}

      {/* Dải chọn game — hàng phụ dưới header, chỉ hiện ở trang chủ.
          Không đặt z-index: khi cuộn, .navbar chuyển sang fixed và phải nằm đè lên dải này. */}
      {isHome && (
        <nav className="game-bar" aria-label="Chọn game để thuê acc">
          <span className="game-bar__label">Thuê acc</span>
          <div className="game-bar__list">
            {GAMES.map((game) => {
              const label = (
                <>
                  {game.label}
                  {game.key === HOT_GAME && (
                    <span className="hl-hot game-bar__hot" aria-hidden="true">
                      HOT
                    </span>
                  )}
                </>
              );
              /* Dải này là BỘ LỌC của trang chủ, không phải menu điều hướng:
                 bấm vào là đổi tab rồi cuộn xuống danh sách acc (phần cuộn nằm
                 ở Homepage.jsx, kích hoạt khi ?game= trên URL đổi).

                 Từng có lúc tôi đổi nó thành link sang trang trụ cột
                 (/thue-acc-dot-kich/) để dồn link nội bộ cho trang mang từ
                 khoá — làm vậy là mất hành vi lọc + cuộn, người dùng bị đá
                 sang một trang khác. Link cho trang trụ cột lấy ở footer
                 (sites.json → footerLinks), có mặt trên mọi trang, không phải
                 giành chỗ của dải lọc này. */
              return (
                <Link
                  key={game.key}
                  to={`/?game=${game.slug}`}
                  className={`game-bar__item ${activeGame === game.key ? "is-active" : ""}`}
                  aria-current={activeGame === game.key ? "true" : undefined}
                  onClick={closeMobileMenu}
                >
                  {label}
                </Link>
              );
            })}
          </div>
        </nav>
      )}

      {/* Dim overlay behind sidebar — click để đóng; bàn phím dùng Escape/nút đóng */}
      <div
        className={`mobile-menu-overlay ${isMobileMenuOpen ? "is-open" : ""}`}
        onClick={closeMobileMenu}
        aria-hidden="true"
      />

      {/* Mobile Menu Sidebar */}
      <div
        id="mobile-menu"
        className={`mobile-menu ${isMobileMenuOpen ? "is-open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label="Menu điều hướng"
      >
        <button ref={menuCloseRef} className="mobile-menu-close" onClick={closeMobileMenu} aria-label="Đóng menu">
          <span aria-hidden="true">✕</span>
        </button>
        <Link to="/" onClick={closeMobileMenu}>
          Trang Chủ
        </Link>
        <Link to="/cong-dong" onClick={closeMobileMenu}>
          Cộng Đồng
        </Link>
        <Link to="/hong-bien" onClick={closeMobileMenu}>
          Hóng Biến
        </Link>
        <Link to="/huong-dan" onClick={closeMobileMenu}>
          Hướng Dẫn
        </Link>
        <div className="mobile-dropdown">
          <button
            className="mobile-dropdown__toggle"
            onClick={() => setIsMobileDownloadOpen(!isMobileDownloadOpen)}
            aria-expanded={isMobileDownloadOpen}
            aria-controls="mobile-download-menu"
          >
            Tải Phần Mềm
            <i className={`nav-dropdown__arrow ${isMobileDownloadOpen ? "is-open" : ""}`} aria-hidden="true" />
          </button>
          <div id="mobile-download-menu" className={`mobile-dropdown__menu ${isMobileDownloadOpen ? "is-open" : ""}`}>
            <a href="/downloads/AnyDesk.exe" download rel="nofollow" onClick={closeMobileMenu}>
              <span className="mobile-dropdown__sub">Remote Desktop</span>
              Tải AnyDesk
            </a>
            <a href="/downloads/Aweray_Remote.exe" download rel="nofollow" onClick={closeMobileMenu}>
              <span className="mobile-dropdown__sub">Remote Desktop</span>
              Tải AweSun
            </a>
            <a href="/downloads/zApp01092026.rar" download rel="nofollow" onClick={closeMobileMenu}>
              <span className="mobile-dropdown__sub">Thuê Acc CF</span>
              App Thuê Acc
            </a>
          </div>
        </div>
        <a
          href="https://www.facebook.com/ThueAcCF"
          target="_blank"
          rel="noreferrer noopener"
          onClick={() => {
            window.gtag_report_conversion_contact?.();
            if (SITE.id === "com") window.gtag_report_conversion_contact2?.();
            closeMobileMenu();
          }}
        >
          Liên Hệ Admin
        </a>
      </div>
    </>
  );
}
